import { useMemo } from 'react';
import { Box, Fade, Paper, Typography, useTheme } from '@material-ui/core';
import { FormSummaryQuestionTitle } from './FormSummaryQuestionTitle';
import { DateListDataPoint } from '@vyce/core/src/types';
import { FormSummaryDateAnswer } from './FormSummaryDateAnswer';
import { formatFormTimeLabel, parseFormTime } from '../utils/formSummaryUtils';

interface TimeGroup {
  hour: string;
  times: Array<{ time: string; count: number }>;
}

export const FormSummaryTime = ({
  title,
  totalAnswers,
  data,
  description,
  videoLink,
  photoUrl,
  explanation,
  pdfUrl,
  onItemClick,
}: {
  title: string;
  totalAnswers: number;
  data: DateListDataPoint[];
  description?: string;
  videoLink?: string;
  photoUrl?: string;
  explanation?: string;
  pdfUrl?: string;
  onItemClick?: (answerValue: string, answerLabel: string) => void;
}) => {
  const theme = useTheme();

  const groupedData = useMemo(() => {
    const timeCounts = new Map<string, number>();
    data.forEach(item => {
      const count = timeCounts.get(item.value as string) || 0;
      timeCounts.set(item.value as string, count + 1);
    });

    const hourGroups = new Map<string, Map<string, number>>();
    timeCounts.forEach((count, timeValue) => {
      const parsedTime = parseFormTime(timeValue);
      if (!parsedTime.isValid()) return;
      const hour = parsedTime.format('HH');
      if (!hourGroups.has(hour)) {
        hourGroups.set(hour, new Map());
      }
      hourGroups.get(hour)!.set(timeValue, count);
    });

    const result: TimeGroup[] = Array.from(hourGroups.entries())
      .map(([hour, times]) => ({
        hour,
        times: Array.from(times.entries())
          .map(([time, count]) => ({ time, count }))
          .sort((a, b) => a.time.localeCompare(b.time)),
      }))
      .sort((a, b) => a.hour.localeCompare(b.hour));

    return result;
  }, [data]);

  return (
    <Fade in={true} timeout={200} style={{ width: '100%' }}>
      <Paper variant="outlined" style={{ padding: 24, width: '100%' }}>
        <FormSummaryQuestionTitle
          title={title}
          totalAnswers={totalAnswers}
          description={description}
          videoLink={videoLink}
          photoUrl={photoUrl}
          explanation={explanation}
          pdfUrl={pdfUrl}
        />

        <Box marginTop={2}>
          {groupedData.map((group, groupIndex) => (
            <Box
              key={group.hour}
              style={{
                borderBottom:
                  groupIndex < groupedData.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                paddingBottom: groupIndex < groupedData.length - 1 ? 16 : 0,
                paddingTop: groupIndex > 0 ? 16 : 0,
              }}
            >
              <Box display="flex" style={{ gap: 16 }}>
                <Box style={{ minWidth: 80, paddingTop: 8 }}>
                  <Typography variant="body2" style={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    {parseFormTime(`${group.hour}:00`).format('h A')}
                  </Typography>
                </Box>

                <Box display="flex" flexWrap="wrap" style={{ gap: 8, flex: 1 }}>
                  {group.times.map(({ time, count }) => (
                    <Box key={time}>
                      <FormSummaryDateAnswer
                        value={formatFormTimeLabel(time)}
                        count={count}
                        onClick={
                          onItemClick ? () => onItemClick(time, formatFormTimeLabel(time)) : undefined
                        }
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>
    </Fade>
  );
};
